/**
 * SurveyTrajectory — the three trajectory plots (§5): plan view (E vs N),
 * vertical section vs TVD, and DLS vs MD. Each is a token-themed Plotly chart
 * via ChartFrame (the viz stack, §15) with REQUIRED axis labels (charter §20
 * punch-list 5/9). Depth axes read 0-at-top increasing downward (§20 item 8).
 *
 * The FE plots ONLY what the server computed (tvd/ns/ew/dls come back on the
 * station rows) — no min-curvature math happens here. Points are supplied
 * pre-ordered by MD; freshly-pasted stations without computed columns are
 * excluded upstream (trajectoryPoints).
 */
import type { Data } from "plotly.js-dist-min";
import { ChartFrame, useChartTheme } from "@components/chart";
import { useLocale } from "@hooks/useLocale";
import { DLS_SOFT_WARN, type TrajectoryPoint } from "./surveyMetrics";

interface Props {
  points: TrajectoryPoint[];
  /** Display unit symbol for length axes (e.g. "ft" / "m"), from UOM. */
  lengthUnit: string;
}

export function SurveyTrajectory({ points, lengthUnit }: Props) {
  const { t } = useLocale();
  const theme = useChartTheme();
  const u = lengthUnit ? ` (${lengthUnit})` : "";

  if (points.length === 0) {
    return (
      <div className="empty-state" style={{ marginTop: 16 }}>
        {t("surveys.plot.empty")}
      </div>
    );
  }

  // Vertical section = horizontal displacement from surface (√(ns²+ew²)). The
  // server owns closure math; this is a simple radial distance for the section
  // abscissa and matches the wireframe's "horizontal displacement" axis.
  const vsection = points.map((p) => Math.hypot(p.ns, p.ew));

  const planTrace: Data = {
    type: "scatter",
    mode: "lines+markers",
    x: points.map((p) => p.ew),
    y: points.map((p) => p.ns),
    name: t("surveys.plot.wellpath"),
    line: { width: 2.5, shape: "spline" },
    marker: { size: 4 },
    hovertemplate: `E %{x:,.0f} · N %{y:,.0f}<extra></extra>`,
  };

  const sectionTrace: Data = {
    type: "scatter",
    mode: "lines+markers",
    x: vsection,
    y: points.map((p) => p.tvd),
    name: t("surveys.plot.wellpath"),
    line: { width: 2.5, shape: "spline" },
    marker: { size: 4 },
    hovertemplate: `VS %{x:,.0f} · TVD %{y:,.0f}<extra></extra>`,
  };

  const dlsTrace: Data = {
    type: "scatter",
    mode: "lines+markers",
    x: points.map((p) => p.md),
    y: points.map((p) => p.dls ?? 0),
    name: t("surveys.plot.dls"),
    line: { width: 2 },
    marker: { size: 4 },
    hovertemplate: `MD %{x:,.0f} · DLS %{y:.2f}<extra></extra>`,
  };

  return (
    <div className="surveys-plots">
      <div className="grid-2">
        <ChartFrame
          title={t("surveys.plot.plan")}
          subtitle={t("surveys.plot.plan.sub")}
          data={[planTrace]}
          xTitle={`${t("surveys.axis.easting")}${u}`}
          yTitle={`${t("surveys.axis.northing")}${u}`}
          ariaLabel={t("surveys.plot.plan.aria")}
        />
        <ChartFrame
          title={t("surveys.plot.section")}
          subtitle={t("surveys.plot.section.sub")}
          data={[sectionTrace]}
          xTitle={`${t("surveys.axis.vsection")}${u}`}
          yTitle={`${t("surveys.axis.tvd")}${u}`}
          // Depth axis: 0 at top, increasing downward (§20 item 8).
          layout={{ yaxis: { autorange: "reversed" } }}
          ariaLabel={t("surveys.plot.section.aria")}
        />
      </div>
      <ChartFrame
        title={t("surveys.plot.dlsTitle")}
        subtitle={t("surveys.plot.dls.sub")}
        data={[dlsTrace]}
        xTitle={`${t("surveys.axis.md")}${u}`}
        yTitle={t("surveys.axis.dls")}
        layout={{
          // Soft-warn threshold line at 1.5°/100 length.
          shapes: [
            {
              type: "line",
              xref: "paper",
              x0: 0,
              x1: 1,
              yref: "y",
              y0: DLS_SOFT_WARN,
              y1: DLS_SOFT_WARN,
              line: { color: theme.warn, width: 1, dash: "dash" },
            },
          ],
        }}
        ariaLabel={t("surveys.plot.dls.aria")}
      />
    </div>
  );
}
